import { Box, Grid, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import React, { useState, useEffect } from "react";
import { getActiveProjects } from "../../api/projectApi";
import { getUserProject } from "../../api/userApi";
import { profiles, ADMIN_SISTEMA } from "../../contexts/constants/profiles";
import { getEquipmentCalendar } from "../../api/equipmentCalendarApi";
import { Project } from "../../types/projectTypes";
import { equipmentCalendary } from "../../types/equipmentCalendaryTypes";
import { getProjectActivities } from "../../api/activityApi";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import logo from "../../assets/images/logoElecnor.png";

const EquipmentCalendar: React.FC = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<number>();
    // Cambio de nombre de estado para claridad
    const [calendarData, setCalendarData] = useState<equipmentCalendary[]>([]);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [filterTrigger, setFilterTrigger] = useState(false);
    const [loading, setLoading] = useState(false);
    const [userRole, setUserRole] = useState<string>("");
    const [tableData, setTableData] = useState<Record<string, Record<string, string>>>({});
    // "equipments" guardará la llave única "Nombre - Código"
    const [equipments, setEquipments] = useState<string[]>([]);
    const [dates, setDates] = useState<string[]>([]);

    useEffect(() => {
        fetchProjects();
    }, []);

    useEffect(() => {
        fetchCalendarData();
    }, [filterTrigger]);

    // ... fetchProjects es idéntico al de empleados ...
    const fetchProjects = async () => {
        try {
            setLoading(true);
            const userStr = localStorage.getItem("user");
            let role = "";
            let rut = "";
            if (userStr) {
                const user = JSON.parse(userStr);
                role = user.rols?.description;
                rut = user.identificationNum;
                setUserRole(role);
            }
            let projectsData: Project[] = [];
            if (role === profiles[0].name || role === profiles[2].name || role === profiles[3].name || role === ADMIN_SISTEMA) {
                projectsData = await getActiveProjects();
            } else {
                const userProjects = await getUserProject(rut);
                projectsData = Array.isArray(userProjects) ? userProjects : [];
            }
            const validProjects = projectsData.filter((p) => p.status === undefined || p.status === true);
            setProjects(validProjects);
        } catch (error) {
            console.error("Error al obtener proyectos:", error);
            setProjects([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDateDisplay = (dateStr: string): string => {
        if (!dateStr) return "";
        const [year, month, day] = dateStr.split("-");
        return `${day}/${month}/${year}`;
    };

    const handleProjectChange = (event: SelectChangeEvent<number>) => {
        setSelectedProject(Number(event.target.value));
    };

    const formatDateTime = (dateString: string): string => {
        if (!dateString) return "";
        return `${dateString}T00:00:00`;
    };

    const fetchCalendarData = async () => {
        if (!selectedProject) return;

        try {
            setLoading(true);

            // Obtenemos actividades validas (CIPs)
            const activitiesMaster = await getProjectActivities(selectedProject);
            const validCips = activitiesMaster
                .filter(item => item.activity.addProduction || item.activity.cip === "LT8.10.3")
                .map(item => item.activity.cip);

            const formattedStartDate = formatDateTime(startDate);
            const formattedEndDate = formatDateTime(endDate);

            // LLAMADA A LA API DE EQUIPOS
            const data: equipmentCalendary[] = await getEquipmentCalendar(formattedStartDate, formattedEndDate, selectedProject);

            const filteredData = data.filter(entry => {
                if (!entry.cip) return false;
                return validCips.includes(entry.cip);
            });

            const uniqueDates = Array.from(new Set(filteredData.map(entry => entry.activityDate.split("T")[0]))).sort();

            // CREAMOS LLAVE ÚNICA: "NombreEquipo - CodigoInterno"
            const uniqueEquipments = Array.from(
                new Set(filteredData.map(entry => `${entry.name} - ${entry.internalCode}`))
            );

            const structuredData: Record<string, Record<string, string>> = {};
            uniqueDates.forEach(date => {
                structuredData[date] = {};
                uniqueEquipments.forEach(equipIdentifier => {
                    const entry = filteredData.find(e =>
                        e.activityDate.startsWith(date) &&
                        `${e.name} - ${e.internalCode}` === equipIdentifier
                    );
                    structuredData[date][equipIdentifier] = entry ? entry.cip : "-";
                });
            });

            setCalendarData(filteredData);
            setDates(uniqueDates);
            setEquipments(uniqueEquipments);
            setTableData(structuredData);
        } catch (error) {
            console.error("Error al obtener datos del calendario de equipos:", error);
        } finally {
            setLoading(false);
        }
    };

    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Calendario de Equipos");

        const imageResponse = await fetch(logo);
        const imageBuffer = await imageResponse.arrayBuffer();
        const imageId = workbook.addImage({
            buffer: imageBuffer,
            extension: "png",
        });

        worksheet.addImage(imageId, {
            tl: { col: 0, row: 0 },
            ext: { width: 160, height: 60 },
        });

        worksheet.addRow([]);
        worksheet.addRow([]);
        worksheet.addRow([]);
        worksheet.addRow([]);

        // CAMBIO: Encabezados para equipos
        const headerRow = ["Equipo", "Cód. Interno", ...dates.map(date => formatDateDisplay(date))];
        const headerRowExcel = worksheet.addRow(headerRow);

        headerRowExcel.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFFFF" } };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "0070C0" } };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.border = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
        });

        equipments.forEach((equipKey, rowIndex) => {
            // SEPARAR NOMBRE Y CODIGO
            const separatorIndex = equipKey.lastIndexOf(" - ");
            const name = equipKey.substring(0, separatorIndex);
            const code = equipKey.substring(separatorIndex + 3);

            const rowData = [name, code, ...dates.map(date => tableData[date]?.[equipKey] || "-")];
            const rowExcel = worksheet.addRow(rowData);

            rowExcel.eachCell((cell, colIndex) => {
                cell.border = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
                if (rowIndex % 2 === 0) {
                    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "DCE6F1" } };
                }
                if (colIndex >= 2) {
                    cell.alignment = { horizontal: "center" };
                }
            });
        });

        worksheet.getColumn(1).width = 30; // Equipo
        worksheet.getColumn(2).width = 15; // Codigo
        for (let i = 3; i <= headerRow.length; i++) worksheet.getColumn(i).width = 12;

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, `Calendario_Equipos.xlsx`);
    };

    return (
        <Box sx={{ p: 3 }}>
            {loading && <CircularProgress />}
            {/* Filtros */}
            <Grid container spacing={2} sx={{ marginBottom: 1 }}>
                <Grid item xs={4}>
                    <FormControl fullWidth sx={{ marginBottom: 3 }}>
                        <InputLabel>Seleccionar Proyecto</InputLabel>
                        <Select label="Seleccionar Proyecto" value={selectedProject || ""} onChange={handleProjectChange}>
                            {projects.map((project) => (
                                <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={4}>
                    <TextField label="Fecha de Inicio" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={4}>
                    <TextField label="Fecha de Término" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
                </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
                <Button variant="contained" color="primary" onClick={() => setFilterTrigger(prev => !prev)}>Filtrar</Button>
            </Box>

            {/* Tabla */}
            <TableContainer component={Paper} sx={{ mt: 3, margin: "auto", maxWidth: "100%", overflow: "auto", maxHeight: "calc(100vh - 300px)", boxShadow: 3 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow sx={{ height: "32px" }}>
                            {/* COLUMNA 1: EQUIPO */}
                            <TableCell sx={{ fontSize: "0.8rem", padding: "4px", fontWeight: "bold", position: "sticky", left: 0, top: 0, backgroundColor: "#78a1bf", color: "#004F86", borderRight: "1px solid #ccc", zIndex: 5, whiteSpace: "nowrap", minWidth: "200px", maxWidth: "200px" }}>
                                <strong>Equipo</strong>
                            </TableCell>
                            {/* COLUMNA 2: CODIGO INTERNO */}
                            <TableCell sx={{ fontSize: "0.8rem", padding: "4px", fontWeight: "bold", position: "sticky", left: "200px", top: 0, backgroundColor: "#78a1bf", color: "#004F86", borderRight: "1px solid #ccc", zIndex: 5, whiteSpace: "nowrap", minWidth: "120px", textAlign: "center" }}>
                                <strong>Cód. Interno</strong>
                            </TableCell>
                            {/* FECHAS */}
                            {dates.map((date) => (
                                <TableCell key={date} align="center" sx={{ fontSize: "0.8rem", padding: "4px", fontWeight: "bold", border: "1px solid #ccc", textAlign: "center", backgroundColor: "#78a1bf", color: "#004F86", top: 0, zIndex: 4, minWidth: "80px" }}>
                                    <strong>{formatDateDisplay(date)}</strong>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {equipments.map((equipKey) => {
                            const separatorIndex = equipKey.lastIndexOf(" - ");
                            const name = equipKey.substring(0, separatorIndex);
                            const code = equipKey.substring(separatorIndex + 3);

                            return (
                                <TableRow key={equipKey} sx={{ height: "32px" }}>
                                    <TableCell sx={{ fontSize: "0.8rem", padding: "4px", fontWeight: "bold", position: "sticky", left: 0, backgroundColor: "#f3f3f3", borderRight: "1px solid #ccc", zIndex: 2, whiteSpace: "nowrap", minWidth: "200px", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }} title={name}>
                                        {name}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", padding: "4px", fontWeight: "bold", position: "sticky", left: "200px", backgroundColor: "#f3f3f3", borderRight: "1px solid #ccc", zIndex: 2, whiteSpace: "nowrap", minWidth: "120px", textAlign: "center" }}>
                                        {code}
                                    </TableCell>
                                    {dates.map((date) => (
                                        <TableCell key={date} align="center" sx={{ fontSize: "0.8rem", padding: "4px" }}>
                                            {tableData[date]?.[equipKey] || "-"}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box display="flex" justifyContent="flex-end" marginTop={2}>
                <Button variant="contained" color="primary" onClick={() => exportToExcel()} sx={{ mt: 2 }} disabled={userRole === profiles[3].name}>
                    Descargar a Excel
                </Button>
            </Box>
        </Box>
    );
};

export default EquipmentCalendar;